import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import {
  AuctionStatus,
  AuctionStatusAnalyticsColors,
  AuctionStatusName,
} from '../../constants/AuctionStatus';
import {
  BidStatusType,
  BidStatusTypeAnalyticsColors,
  BidStatusTypeName,
} from '../../constants/BidStatusType';
import {
  fetchOrganizationDetails,
  fetchOrganizationLocations,
  fetchOrganizationStatistics,
} from '../../settings/SettingsActions';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import CommonButton from '../../common/components/CommonButton';
import downloadIcon from '../../assets/img/download.svg';
import AnalyticsLayout from '../components/AnalyticsLayout';
import usePDFExport from '../hooks/usePDFExport';
import { useMediaQuery } from '../../common/hooks/useMediaQuery';
import FormSelectField from '../../common/components/formFields/FormSelectField';
import {
  cookieToLocationId,
  LOCATION_ALL_COOKIE_VALUE,
  LOCATION_ID_COOKIE,
  setLocationIdCookie,
} from '../../constants/LocationCookie';

const ALL_LOCATIONS_OPTION = { value: null, label: 'All' };

const penceToPoundsString = (amountInPence) => {
  const isNum = typeof amountInPence === 'number' && Number.isFinite(amountInPence);
  const pounds = isNum ? Math.round(amountInPence / 100) : 0;
  return pounds.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const launchedAuctionsDataBase = [
  {
    type: AuctionStatus.PUBLISHED,
    name: AuctionStatusName.PUBLISHED,
    color: AuctionStatusAnalyticsColors.PUBLISHED,
    value: 0,
  },
  {
    type: AuctionStatus.TO_ACCEPT,
    name: AuctionStatusName.TO_ACCEPT,
    color: AuctionStatusAnalyticsColors.TO_ACCEPT,
    value: 0,
  },
  {
    type: AuctionStatus.DRAFT,
    name: AuctionStatusName.DRAFT,
    color: AuctionStatusAnalyticsColors.DRAFT,
    value: 0,
  },
  {
    type: AuctionStatus.ENDED,
    name: AuctionStatusName.ENDED,
    color: AuctionStatusAnalyticsColors.ENDED,
    value: 0,
  },
  {
    type: AuctionStatus.WAITING,
    name: AuctionStatusName.SCHEDULED,
    color: AuctionStatusAnalyticsColors.WAITING,
    value: 0,
  },
  {
    type: AuctionStatus.CANCELLED,
    name: AuctionStatusName.CANCELLED,
    color: AuctionStatusAnalyticsColors.CANCELLED,
    value: 0,
  },
  {
    type: AuctionStatus.REJECTED,
    name: AuctionStatusName.REJECTED,
    color: AuctionStatusAnalyticsColors.REJECTED,
    value: 0,
  },
];

const placedBidsDataBase = [
  {
    type: BidStatusType.WON,
    name: BidStatusTypeName.WON,
    color: BidStatusTypeAnalyticsColors.WON,
    value: 0,
  },
  {
    type: BidStatusType.LOST,
    name: BidStatusTypeName.LOST,
    color: BidStatusTypeAnalyticsColors.LOST,
    value: 0,
  },
  {
    type: BidStatusType.WAITING,
    name: BidStatusTypeName.WAITING,
    color: BidStatusTypeAnalyticsColors.WAITING,
    value: 0,
  },
  {
    type: BidStatusType.REJECTED,
    name: BidStatusTypeName.REJECTED,
    color: BidStatusTypeAnalyticsColors.REJECTED,
    value: 0,
  },
];

const SUMMARY_CONFIG = {
  seller: [
    { title: 'Sold Vehicles', key: 'soldVehicles' },
    { title: 'Sales Revenue', key: 'salesRevenueInPence' },
  ],
  buyer: [
    { title: 'Purchased Vehicles', key: 'purchasedVehicles' },
    { title: 'Purchase Costs', key: 'purchaseCostsInPence' },
  ],
};

function AnalyticsPage() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.get('isAuthenticated'));
  const isBuyer = useSelector((state) => state.auth.get('isBuyer'));
  const isSeller = useSelector((state) => state.auth.get('isSeller'));
  const organizationStatistics = useSelector((state) =>
    state.myOrganization.get('organizationStatistics'),
  );
  const organizationLocations = useSelector((state) =>
    state.myOrganization.get('organizationLocations'),
  );

  const isMobile = useMediaQuery('(max-width: 768px)');
  const { exportAsPDF } = usePDFExport();
  const firstPageRef = useRef(null);

  const locationOptions = useMemo(
    () => [
      ALL_LOCATIONS_OPTION,
      ...(Array.isArray(organizationLocations) ? organizationLocations : []).map((loc) => ({
        value: loc.id,
        label: loc.name || loc.address || loc?.postalCode?.postcode,
        isMain: loc.isMain,
      })),
    ],
    [organizationLocations],
  );

  const hasSingleLocation =
    (Array.isArray(organizationLocations) ? organizationLocations.length : 0) <= 1;

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchOrganizationDetails());
      dispatch(fetchOrganizationLocations());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !Array.isArray(organizationLocations)) return;
    const locationParam = hasSingleLocation
      ? null
      : cookieToLocationId(Cookies.get(LOCATION_ID_COOKIE));
    dispatch(fetchOrganizationStatistics(locationParam));
  }, [isAuthenticated, organizationLocations, hasSingleLocation, dispatch]);

  const handleLocationChange = (option) => {
    setLocationIdCookie(option?.value);
    dispatch(fetchOrganizationStatistics(option?.value || null));
  };

  const statisticsDecorated = useMemo(() => {
    const stats = organizationStatistics || {};
    return {
      ...stats,
      salesRevenueInPence: `£${penceToPoundsString(stats.salesRevenueInPence)}`,
      purchaseCostsInPence: `£${penceToPoundsString(stats.purchaseCostsInPence)}`,
    };
  }, [organizationStatistics]);

  const summaryData = useMemo(
    () => [
      ...(isSeller ? SUMMARY_CONFIG.seller : []),
      ...(isBuyer ? SUMMARY_CONFIG.buyer : []),
    ],
    [isSeller, isBuyer],
  );

  const auctionsData = useMemo(
    () =>
      launchedAuctionsDataBase.map((item) => ({
        ...item,
        value: organizationStatistics?.auctions?.[item.type] || 0,
      })),
    [organizationStatistics],
  );

  const bidData = useMemo(
    () =>
      placedBidsDataBase.map((item) => ({
        ...item,
        value: organizationStatistics?.auctionBids?.[item.type] || 0,
      })),
    [organizationStatistics],
  );

  const auctionsDataTotal = organizationStatistics?.auctionCount || 0;
  const bidsDataTotal = organizationStatistics?.auctionBidCount || 0;

  const soldCount = organizationStatistics?.auctions?.[AuctionStatus.SOLD] || 0;
  const wonCount = organizationStatistics?.auctionBids?.[BidStatusType.WON] || 0;

  const handlePDFExport = () => exportAsPDF(firstPageRef);

  const mainLocation = useMemo(
    () => locationOptions.find((it) => it.isMain === true) || null,
    [locationOptions],
  );

  const cookieBasedLocation = useMemo(() => {
    const rawCookie = Cookies.get(LOCATION_ID_COOKIE);
    if (rawCookie === LOCATION_ALL_COOKIE_VALUE) {
      return ALL_LOCATIONS_OPTION;
    }
    return locationOptions.find((it) => it.value === rawCookie) || null;
  }, [locationOptions]);

  const onlyRealOption = locationOptions.find((opt) => opt.value) || null;

  const selectedLocationOption =
    hasSingleLocation && onlyRealOption ? onlyRealOption : cookieBasedLocation || mainLocation;

  return (
    <section className="analytics-page">
      <div className="analytics-wrapper">
        <div className="analytics-header">
          <h3 className="analytics-header-title">Analytics</h3>
          <div className="analytics-buttons-wrapper">
            <Formik
              enableReinitialize
              initialValues={{
                location: selectedLocationOption,
              }}
              onSubmit={() => {}}
            >
              <Form noValidate>
                <FormSelectField
                  name="location"
                  rowClassNames="analytics-buttons-wrapper__location-select"
                  options={locationOptions}
                  labelVariant="bordered"
                  disabled={hasSingleLocation}
                  onChangeHandler={handleLocationChange}
                />
              </Form>
            </Formik>
            {!isMobile && (
              <CommonButton
                type="button"
                label="PDF FILE"
                className="analytics-header-button"
                variant={CommonButtonVariants.OUTLINED}
                handleClick={handlePDFExport}
                icon={downloadIcon}
                iconAlt="Download PDF"
                iconLeft
              />
            )}
            <CommonButton
              type="button"
              label="CSV FILE"
              className={classNames('analytics-header-button', 'd-none')}
              variant={CommonButtonVariants.OUTLINED}
              handleClick={() => {}}
              icon={downloadIcon}
              iconAlt="Download CSV"
              iconLeft
            />
          </div>
        </div>
        <AnalyticsLayout
          summaryData={summaryData}
          statistics={statisticsDecorated}
          auctionsDataTotal={auctionsDataTotal}
          bidsDataTotal={bidsDataTotal}
          auctionsData={auctionsData}
          bidData={bidData}
          soldCount={soldCount}
          wonCount={wonCount}
          showAuctionsChart={isSeller}
          showBidsChart={isBuyer}
        />
      </div>

      {/* Hidden PDF DOM clone */}
      <div className="analytics-pdf-wrapper">
        <div className="analytics-pdf-page" ref={firstPageRef}>
          <AnalyticsLayout
            summaryData={summaryData}
            statistics={statisticsDecorated}
            auctionsDataTotal={auctionsDataTotal}
            bidsDataTotal={bidsDataTotal}
            auctionsData={auctionsData}
            bidData={bidData}
            soldCount={soldCount}
            wonCount={wonCount}
            showAuctionsChart={isSeller}
            showBidsChart={isBuyer}
          />
        </div>
      </div>
    </section>
  );
}

export default AnalyticsPage;
